// @vendors
const Immutable = require('immutable');
const moment = require('moment');

// @constants
const actionTypes = require('../raConstants/actionTypes');
const {
    CONTRIBUTION_PERIOD_MONTHLY,
    MINIMUM_OBJECTIVE_AMOUNT,
} = require('../raConstants/index');

// utilities
const { getDay, todayDate } = require('utilities/dateHelper');
const { getPageScrollHeight } = require('../utilities/RAScrollUtils');

const initialState = Immutable.fromJS({
    clientRiskProfile: '',
    clientRiskProfileDescription: '',
    maxInvestmentProfile: 0,
    selectedStrategy: 0,
    objective: '',
    objectiveAmount: 0,
    objectiveAmountCurrency: 'EUR',
    objectiveEndDate: null,
    objectiveEndDateError: false,
    objectiveType: '',
    initialContributionAmount: '',
    recurringContributionAmount: 0,
    periodicity: CONTRIBUTION_PERIOD_MONTHLY,
    contributionDay: getDay(todayDate()),
    isFetchingProfile: false,
    isSavingSimulation: false,
    saveSuccess: false,
    error: null,
    errorAmountMin: false,
    errorInitialMin: false,
    errorPeriodicityMin: false,
    isSaveSimulationModalOpen: false,
    isSaveSimulationButtonDisabled: true,
    pageScrollHeight: 0,
});

const parseGoalKey = {
    investmentAmount: 'objectiveAmount',
    investmentTitle: 'objective',
    investmentDate: 'objectiveEndDate',
};

const RACustomPlanByObjective = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_UPDATE_DESCRIPTION:
            return state.merge({
                objective: action.payload.title,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_OBJECTIVE_AMOUNT:
            const errorAmount =
                action.payload.amount !== 0 &&
                action.payload.amount < MINIMUM_OBJECTIVE_AMOUNT;
            return state.merge({
                objectiveAmount: action.payload.amount,
                errorAmountMin: errorAmount,
            });
        case actionTypes.RA_SET_GOAL_SET_INVESTMENT_GOAL_VALUE:
            const { key, value } = action.payload;
            const goalKey = parseGoalKey[key];
            const parsedValue =
                goalKey === 'objectiveEndDate'
                    ? moment(value).startOf('day')
                    : value;
            return state.merge({
                [goalKey]: parsedValue,
                objectiveEndDateError:
                    goalKey === 'objectiveEndDate'
                        ? !value || value === 'Invalid date'
                        : state.objectiveEndDateError,
            });
        case actionTypes.RA_UPDATE_SELECTED_GOAL:
            const { goalSelected } = action.payload;
            const currentObjectiveType = state.get('objectiveType');
            const newObjectiveType =
                currentObjectiveType !== goalSelected.id ? goalSelected.id : '';
            const newObjectiveName =
                (!!newObjectiveType && goalSelected.label) || '';

            return state.merge({
                objectiveType: newObjectiveType,
                objective: newObjectiveName,
                objectiveAmount: 0,
                objectiveEndDate: null,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_OBJECTIVE_END_DATE:
            return state.merge({
                objectiveEndDate: action.payload.date,
                objectiveEndDateError: !action.payload.date.isValid(),
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_END_DATE_ERROR_ENABLED:
            return state.merge({
                objectiveEndDateError: true,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_INITIAL_CONTRIBUTION_AMOUNT:
            const errorFlagContribution =
                action.payload.investment !== 0 &&
                action.payload.investment < MINIMUM_OBJECTIVE_AMOUNT;
            return state.merge({
                initialContributionAmount: action.payload.investment,
                errorInitialMin: errorFlagContribution,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_RECURRING_CONTRIBUTION_AMOUNT:
            const errorFlagRecurring =
                action.payload.amount !== 0 && action.payload.amount < 1;
            return state.merge({
                recurringContributionAmount: action.payload.amount,
                errorPeriodicityMin: errorFlagRecurring,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_PERIODICITY:
            return state.merge({
                periodicity: action.payload.periodicity,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_CONTRIBUTION_DAY:
            return state.merge({
                contributionDay: action.payload.day,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_SELECTED_STRATEGY:
            return state.set('selectedStrategy', action.payload);
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_DATA_FROM_SIMULATION:
            const immSimulation = action.payload;
            let newState = state.merge({
                initialContributionAmount: immSimulation.get(
                    'initialContribution',
                ),
                selectedStrategy:
                    immSimulation.get('selectedPortfolio') ||
                    immSimulation.get('maxTypeOfProduct'),
                objective: immSimulation.get('name'),
                objectiveType: immSimulation.get('type'),
                objectiveAmount: immSimulation.get('amount'),
                objectiveEndDate: immSimulation.get('endDate')
                    ? moment(immSimulation.get('endDate'))
                    : null,
                errorAmountMin:
                    immSimulation.get('amount') < MINIMUM_OBJECTIVE_AMOUNT &&
                    immSimulation.get('amount') > 0,
                errorInitialMin:
                    immSimulation.get('initialContribution') <
                        MINIMUM_OBJECTIVE_AMOUNT &&
                    immSimulation.get('initialContribution') > 0,
            });

            if (immSimulation.get('hasRecurringTransfer')) {
                newState = newState.merge({
                    periodicity: immSimulation.getIn([
                        'recurringTransfer',
                        'frequencyCode',
                    ]),
                    recurringContributionAmount: immSimulation.getIn([
                        'recurringTransfer',
                        'amount',
                    ]),
                    contributionDay: immSimulation.getIn([
                        'recurringTransfer',
                        'cutDay',
                    ]),
                    errorPeriodicityMin:
                        immSimulation.getIn(['recurringTransfer', 'amount']) <
                            1 &&
                        immSimulation.getIn(['recurringTransfer', 'amount']) >
                            0,
                });
            }

            return newState;
        case actionTypes.RA_MANAGEMENT_PLAN_GET_INVESTMENT_PROFILE_REQUEST:
            return state.merge({
                isFetchingProfile: true,
            });
        case actionTypes.RA_MANAGEMENT_PLAN_GET_INVESTMENT_PROFILE_FAILURE:
            return state.merge({
                error: action.payload.error,
                isFetchingProfile: false,
                simulationId: '',
            });
        case actionTypes.RA_MANAGEMENT_PLAN_GET_INVESTMENT_PROFILE_SUCCESS:
            const {
                initialContribution,
                selectedPortfolio,
                hasRecurringTransfer,
                name,
                type,
                amount,
                endDate,
                id,
            } = action.payload.data;
            const objectiveType = type ? type.toLowerCase() : '';

            let newCustomState = state.merge({
                initialContributionAmount: initialContribution || '',
                isFetchingProfile: false,
                selectedStrategy: selectedPortfolio || 0,
                objective: name || '',
                objectiveType,
                objectiveAmount: amount || 0,
                objectiveEndDate: endDate ? moment(endDate) : null,
                simulationId: id,
            });

            if (hasRecurringTransfer) {
                const {
                    amount,
                    cutDay,
                    frequencyCode,
                } = action.payload.data.recurringTransfer;

                const recurringTransfer = {
                    periodicity: frequencyCode,
                    recurringContributionAmount: amount,
                    recurringContributionDay: cutDay,
                };
                newCustomState = newCustomState.merge(recurringTransfer);
            }

            return newCustomState;
        case actionTypes.RA_SAVE_SIMULATION_OBJECTIVE_CHANGE_MODAL_OPTION:
            return state.merge({
                isSaveSimulationModalOpen: action.payload.value,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SCHEME_CONTRIBUTION_RESET:
            return state.merge({
                objectiveAmount: 0,
                errorAmountMin: false,
                objectiveEndDate: null,
                objectiveEndDateError: false,
                initialContributionAmount: '',
                errorInitialMin: false,
                recurringContributionAmount: 0,
                errorPeriodicityMin: false,
                periodicity: CONTRIBUTION_PERIOD_MONTHLY,
                contributionDay: getDay(todayDate()),
                selectedStrategy: 0,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_RESET:
        case actionTypes.RA_SAVE_SIMULATION_RESET:
        case actionTypes.RA_SAVE_SIMULATION_OBJECTIVE_VALUE_RESET:
        case actionTypes.RA_SAVE_SIMULATION_REQUEST_SUCCESS_FOR_OBJECTIVE:
            return initialState;
        case actionTypes.RA_SAVE_SIMULATION_OBJECTIVE_DISABLE_SIMULATION_BUTTON:
            return state.merge({
                isSaveSimulationButtonDisabled: action.payload.value,
            });
        case actionTypes.RA_SIMULATION_SET_SCROLL_HEIGHT:
            return state.merge({
                pageScrollHeight: getPageScrollHeight(),
            });
        default:
            return state;
    }
};

module.exports = RACustomPlanByObjective;
